import type { BillingPlanDto } from '@shared/core/billing'
import { CalendarsLimitType } from '@shared/core/billing/calendars-limit-type'
import classNames from 'classnames'
import { Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button, FormattedText } from 'src/modules/core/components'
import { getCalendarsLimitType } from 'src/modules/payment/utils/helpers'
import { getPlanUserSeats } from 'src/modules/payment/utils/plans-offer.utils'
import type { PlanOfferCardProps } from './plan-offer-card.interface'
import classes from './plan-offer-card.module.scss'
import { usePlanOfferPrice } from './use-plan-offer-price'

const strong = (chunks: ReactNode) => <strong>{chunks}</strong>

export function PlanOfferCard({
  plan,
  priceSuffix,
  buttonLabelId,
  isRecommended,
  isCurrentPlan,
  onSelect,
}: PlanOfferCardProps) {
  return (
    <article
      className={classNames(classes.card, { [classes.recommended]: isRecommended })}
      data-testid="plan-offer-card"
      data-plan-id={plan.paddle_id}
    >
      <input type="hidden" name="plan-id" value={String(plan.paddle_id ?? '')} readOnly tabIndex={-1} aria-hidden />
      {isRecommended && (
        <span className={classes.badge}>
          <FormattedText id="payment.plans.card.recommended" />
        </span>
      )}
      <div>
        <h3 className={classes.name}>{plan.name}</h3>
        <p className={classes.tagline}>
          <FormattedText id="payment.plans.card.tagline" />
        </p>
      </div>
      <PlanPrice plan={plan} priceSuffix={priceSuffix} />
      <Button className={classes.button} disabled={isCurrentPlan} onClick={() => onSelect(plan)}>
        <FormattedText id={buttonLabelId} />
      </Button>
      <PlanFeatures plan={plan} priceSuffix={priceSuffix} />
    </article>
  )
}

function PlanPrice({ plan, priceSuffix }: Pick<PlanOfferCardProps, 'plan' | 'priceSuffix'>) {
  const { price, billedYearlyPrice } = usePlanOfferPrice(plan)

  return (
    <div>
      <div className={classes.priceRow}>
        <span className={classNames(classes.price, { [classes.priceLarge]: priceSuffix === 'month' })}>{price}</span>
        <span className={classes.suffix}>
          <FormattedText id={`payment.plans.card.priceSuffix.${priceSuffix}`} />
        </span>
      </div>
      <p className={classes.billed}>
        {billedYearlyPrice ? (
          <FormattedText id="payment.plans.card.billedYearly" values={{ price: billedYearlyPrice }} />
        ) : (
          <FormattedText id="payment.plans.card.billedMonthly" />
        )}
      </p>
    </div>
  )
}

function PlanFeatures({ plan, priceSuffix }: Pick<PlanOfferCardProps, 'plan' | 'priceSuffix'>) {
  const userSeats = getPlanUserSeats(plan)

  return (
    <ul className={classes.features}>
      {plan.reminders_limit != null && (
        <PlanFeature
          id={`payment.plans.card.reminders.${priceSuffix}`}
          values={{ count: plan.reminders_limit, strong }}
        />
      )}
      <PlanCalendarsFeature plan={plan} />
      {userSeats !== undefined && <PlanFeature id="payment.plans.card.users" values={{ count: userSeats, strong }} />}
      <PlanFeature id="payment.plans.card.freeBooking" />
    </ul>
  )
}

function PlanCalendarsFeature({ plan }: { plan: BillingPlanDto }) {
  if (getCalendarsLimitType(plan) === CalendarsLimitType.unlimited) {
    return <PlanFeature id="payment.plans.card.calendarsUnlimited" values={{ strong }} />
  }

  const count = plan.only_primary_calendar ? 1 : plan.calendars_limit

  return <PlanFeature id="payment.plans.card.calendars" values={{ count, strong }} />
}

function PlanFeature({ id, values }: { id: string; values?: Record<string, ReactNode> }) {
  return (
    <li className={classes.feature}>
      <Check size={14} strokeWidth={2.2} className={classes.check} aria-hidden />
      <span>
        <FormattedText id={id} values={values} />
      </span>
    </li>
  )
}
